import React, { useState, useRef } from 'react';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import Label from '../../components/ui/Label';
import { submitContactForm as submitContactFormAPI } from '../../util/api';
import ReCAPTCHA from 'react-google-recaptcha';

import css from './ContactUsPage.module.css';

const ContactFormComponent = props => {
  const { intl } = props;

  const recaptchaRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate reCAPTCHA token
    if (!recaptchaToken) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);
    setShowSuccess(false);

    try {
      // Submit to backend API with recaptcha token
      const data = await submitContactFormAPI({
        ...formData,
        recaptchaToken,
      });

      if (data.success) {
        setShowSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });

        // Reset reCAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setRecaptchaToken(null);

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        // Handle error response
        throw new Error(data.message || 'Failed to submit contact form');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setShowError(true);

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);

      // Hide error message after 5 seconds
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={css.formCard}>
      <CardHeader className={css.formCardHeader}>
        <CardTitle className={css.formCardTitle}>
          <FormattedMessage id="ContactUsPage.formTitle" />
        </CardTitle>
        <CardDescription className={css.formCardDescription}>
          <FormattedMessage id="ContactUsPage.formDescription" />
        </CardDescription>
      </CardHeader>
      <CardContent className={css.formCardContent}>
        <form onSubmit={handleSubmit} className={css.contactForm}>
          {/* Success Message */}
          {showSuccess && (
            <div className={css.successMessage}>
              <FormattedMessage id="ContactUsPage.successMessage" />
            </div>
          )}

          {/* Error Message */}
          {showError && (
            <div className={css.errorMessage}>
              <FormattedMessage id="ContactUsPage.errorMessage" />
            </div>
          )}

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <Label htmlFor="name">
                <FormattedMessage id="ContactUsPage.nameLabel" />
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder={intl.formatMessage({ id: 'ContactUsPage.namePlaceholder' })}
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className={css.formGroup}>
              <Label htmlFor="email">
                <FormattedMessage id="ContactUsPage.emailLabel" />
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={intl.formatMessage({ id: 'ContactUsPage.emailPlaceholder' })}
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className={css.formRow}>
            <div className={css.formGroup}>
              <Label htmlFor="phone">
                <FormattedMessage id="ContactUsPage.phoneLabel" />
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder={intl.formatMessage({ id: 'ContactUsPage.phonePlaceholder' })}
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className={css.formGroup}>
              <Label htmlFor="subject">
                <FormattedMessage id="ContactUsPage.subjectLabel" />
              </Label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className={css.formSelect}
              >
                <option value="">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectPlaceholder' })}
                </option>
                <option value="buying">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectBuying' })}
                </option>
                <option value="selling">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectSelling' })}
                </option>
                <option value="listing">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectListing' })}
                </option>
                <option value="payment">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectPayment' })}
                </option>
                <option value="account">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectAccount' })}
                </option>
                <option value="technical">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectTechnical' })}
                </option>
                <option value="other">
                  {intl.formatMessage({ id: 'ContactUsPage.subjectOther' })}
                </option>
              </select>
            </div>
          </div>

          <div className={css.formGroup}>
            <Label htmlFor="message">
              <FormattedMessage id="ContactUsPage.messageLabel" />
            </Label>
            <Textarea
              id="message"
              name="message"
              placeholder={intl.formatMessage({ id: 'ContactUsPage.messagePlaceholder' })}
              value={formData.message}
              onChange={handleInputChange}
              required
              className={css.formTextarea}
              rows="6"
            />
          </div>

          {/* reCAPTCHA */}
          <div className={css.recaptchaWrapper}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={handleRecaptchaChange}
              onExpired={() => setRecaptchaToken(null)}
              onErrored={() => setRecaptchaToken(null)}
              theme="dark"
            />
          </div>

          <Button
            type="submit"
            className={css.submitButton}
            disabled={isSubmitting || !recaptchaToken}
          >
            {isSubmitting ? (
              <FormattedMessage id="ContactUsPage.submittingButton" />
            ) : (
              <FormattedMessage id="ContactUsPage.submitButton" />
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

ContactFormComponent.propTypes = {
  intl: intlShape.isRequired,
};

const ContactForm = injectIntl(ContactFormComponent);

export default ContactForm;
